import { UniAgFns } from "@/shared/uni-ag-fns";
import { GetRowIdParams, GridOptions, GridReadyEvent, RowNode, RowSelectedEvent } from "@ag-grid-community/core";
import { Location } from "@angular/common";
import { Component, OnInit, Optional, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { InventoryManagerComponent } from "./inventory-manager.component";

import { ActivatedRoute } from "@angular/router";
import { AccountFinderDialogComponent } from "app/admin/account-finder-dialog.component";
import { ProductFinderDialogComponent } from "app/inventory/product-finder-dialog.component";
import { ItemDetailSummary } from "app/model/entities/-item-detail-summary";
import {
  Account,
  Addon,
  BarcodePrintStatusEnum,
  Feature,
  ItemBin,
  ItemDetail,
  ItemDetailAddon,
  ItemDetailStatus,
  ItemDetailStatusEnum,
  Product,
  ProductType,
} from "app/model/entities/entity-model";
import { AddonFinderDialogComponent } from "app/shared/addon-finder-dialog.component";
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox } from "app/shared/ag-fns";
import { BarcodeService } from "app/shared/barcode.service";

import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { EntityFinderDialog } from "app/shared/entity-finder.dialog";
import { EntityState, SaveResult } from "breeze-client";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { EntityFns } from "@/shared/entity-fns";

interface ItemDetailProxy {
  tempId: number;

  qty: number;
  account: Account;
  product: Product;
  itemBin: ItemBin;
  itemDetailAddons: ItemDetailAddonProxy[];
}

interface ItemDetailAddonProxy {
  tempId: number;
  itdProxy: ItemDetailProxy;

  additionalInfo: string;
  addon: Addon;
}

@Component({
  selector: "add-inventory",
  templateUrl: "./add-inventory.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddInventoryComponent extends DomainBaseComponent implements OnInit {
  @ViewChild("delButtonCell") delButtonCell: TemplateRef<any>;
  @ViewChild("issButtonCell") issButtonCell: TemplateRef<any>;
  @ViewChild("accountButtonCell") accountButtonCell: TemplateRef<any>;
  @ViewChild("addModButtonCell") addModButtonCell: TemplateRef<any>;
  @ViewChild("delModButtonCell") delModButtonCell: TemplateRef<any>;
  @ViewChild("embButtonCell") embButtonCell: TemplateRef<any>;

  idsGridOptions: GridOptionsExt;
  currentSummaryItem: ItemDetailSummary;
  currentSummaryRowNode: RowNode;

  newItdGridOptions: GridOptionsExt;
  itdProxies: ItemDetailProxy[] = [];
  currentNewItd: ItemDetailProxy;
  nextTempId = 0;

  allItdStatuses: ItemDetailStatus[];
  selectedItdStatuses: ItemDetailStatus[];

  itemBins: ItemBin[];

  itemDetailStatusDefault: ItemDetailStatus;
  itemBinDefault: ItemBin;

  features: Feature[];

  constructor(
    protected domainService: DomainService,
    protected barcodeService: BarcodeService,
    protected route: ActivatedRoute,
    protected location: Location,
    @Optional() private inventoryManager: InventoryManagerComponent
  ) {
    super(domainService);
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    // Subscribe to button commands from parent menu
    this.inventoryManager?.addBlankItemDetailClicked$.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.addBlankItemDetail();
    });
    this.inventoryManager?.copySelectedItemDetailClicked$.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.addCopyItemDetail();
    });

    // Show the Add Inventory buttons when this page is active (deferred to avoid ExpressionChangedAfterItHasBeenCheckedError)
    if (this.inventoryManager) {
      setTimeout(() => {
        // Reset other pages' controls
        this.inventoryManager.showEoqButtons = false;
        this.inventoryManager.showScannerControls = false;
        this.inventoryManager.showPrintBarcodesButton = false;
        // Show this page's controls
        this.inventoryManager.showAddInventoryButtons = true;
      });
    }
  }

  override ngOnDestroy() {
    // Hide the Add Inventory buttons when leaving this page
    if (this.inventoryManager) {
      this.inventoryManager.showAddInventoryButtons = false;
    }
    super.ngOnDestroy();
  }

  async updateFromContext() {
    this.idsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onIdsGridReady,
      onRowSelected: this.onIdsRowSelected,
      onFilterChanged: this.onFilterChanged,
      getRowId: this.getIdsRowId,
      rowModelType: "serverSide",
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.updateLocation();
        }
      },
    });
    // TODO: 'id' is not right on next line - set getRowId
    AgFns.captureGridRouteParams(this.idsGridOptions, this.route, "id");

    this.newItdGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onNewItdGridReady,
        onRowSelected: this.onNewItdRowSelected,
        getRowId: this.getNewItdRowId,
        rowSelection: RowSelectionMultipleCheckbox,
      },
      { detailProperty: "itemDetailAddons" }
    );
    this.updateForMasterDetail(this.newItdGridOptions);

    this.features = await this.dbQueryService.getFeatures();
    this.allItdStatuses = this.dbQueryService.getAllCached(ItemDetailStatus);

    let statusIds: number[];
    if (this.idsGridOptions.context?.gridState?.fm == null) {
      statusIds = [ItemDetailStatusEnum.InInventory];
      this.selectedItdStatuses = this.allItdStatuses.filter((its) => statusIds.indexOf(its.id) !== -1);
    }

    this.itemBins = await this.dbQueryService.getItemBins();
    this.itemBins = _.sortBy(this.itemBins, (ib) => ib.name);

    this.itemBinDefault = this.itemBins.find((x) => x.id == ItemBin.UnitecDefault);

    this.isPageReady = true;
  }

  async canDeactivate() {
    this.stopEditing();
    const ok = await super.canDeactivate();
    if (!ok) return false;

    if (this.itdProxies.length > 0) {
      const ok = await this.dialogService.yesNoDialog("Changes have not been saved", "Do you want to save before exiting?");
      if (ok) {
        const wasSaved = await this.saveChanges(true);
        if (!wasSaved) return false;
      } else {
        // Not actually needed but for clarity.
        this.cancelChanges();
      }
    }

    this.uow.clearEntities(ItemDetail);
    this.uow.clearEntities(Product);
    this.uow.clearEntities(ProductType);

    return true;
  }

  async onIdsGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      {
        headerName: "Account",
        field: "account.accountName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        ...AgFns.createSetFilterObjectProps("Status", "itemDetailStatus.name", this.allItdStatuses),
        ...AgFns.flexWidth(140),
      },
      { headerName: "Qty", field: "qty", ...AgFns.fixWidth(80) },
    ];

    const sortModel = [
      { colId: "product.productType.manufacturer.name", sort: "asc" },
      { colId: "product.productType.style", sort: "asc" },
    ] as ISortModel;

    // next line is needed by the buildDatasource method
    AgFns.initGrid(event, colDefs, sortModel);
    this.updateDatasource();
    AgFns.applyGridRouteParams(this.idsGridOptions);
  }

  getIdsRowId(params: GetRowIdParams) {
    const data = <ItemDetailSummary>params.data;
    const r = data?.productId.toString() + "-" + data?.accountId?.toString() + "-" + data?.itemDetailStatusId.toString();
    return r;
  }

  getNewItdRowId(params: GetRowIdParams) {
    return params.data?.tempId?.toString();
  }

  onNewItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...AgFns.createCellButtonProps("", this.delButtonCell), ...AgFns.fixWidth(70) },
      { ...AgFns.createCellButtonProps("", this.issButtonCell), ...AgFns.fixWidth(120) },
      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      {
        ...AgFns.createCellButtonProps("", this.accountButtonCell),
        ...AgFns.fixWidth(130),
      },
      { headerName: "Account", field: "account.accountName", ...AgFns.flexWidth(200) },
      {
        headerName: "Item Bin",
        field: "itemBin",
        editable: true,
        ...AgFns.createDropdownEditorObjectProps(this.itemBins, "name"),
        ...AgFns.fixWidth(120),
      },
      { headerName: "Qty", field: "qty", editable: true, cellClass: "cell-editable", ...AgFns.fixWidth(100) },
      {
        ...AgFns.createCellButtonProps("", this.addModButtonCell),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Mods",
        field: "itemDetailAddons.length",
        cellRenderer: "agGroupCellRenderer",
        ...AgFns.fixWidth(120),
      },
      { headerName: "Temp Id", field: "tempId", ...AgFns.fixWidth(120) },
    ];
    const sortFields = [
      "account.accountName",
      "product.productType.manufacturer.name",
      "product.productType.description",
      "product.productType.style",
    ];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" };
    }) as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
  }

  
  updateForMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.getRowId = (params: GetRowIdParams) => {
      return params.data?.tempId.toString();
    };
    detailGridOptions.columnDefs = [
      {
        ...AgFns.createButtonProps("", this.deleteItemDetailAddon.bind(this), {
          label: "Del",
        }),
      },
      {
        ...AgFns.createButtonProps("", this.selectAddon.bind(this), {
          label: "Select Add-On",
        }),
      },
      { headerName: "Addon", field: "addon.nameAndLocation" },
      { headerName: "Addl. Info", field: "additionalInfo", editable: true, cellClass: "cell-editable" },
      { headerName: "TempId", field: "tempId" },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      refreshStrategy: "everything",
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const itd = params.data as ItemDetailProxy;
        params.successCallback(itd.itemDetailAddons);
      },
    };
  }

  updateDatasource() {
    const gridApi = this.idsGridOptions.apiX;
    if (gridApi == null) {
      return;
    }
    const sortModel = [{ colId: "productId", sort: "asc" as const }];

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createItemDetailSummaryQuery(false), sortModel);

    gridApi.setGridOption("serverSideDatasource", ds);
    AgFns.applyGridRouteParams(this.idsGridOptions);
  }

  onFilterChanged() {
    this.updateLocation();
  }

  updateLocation(key: any = null) {
    // Use current path instead of hardcoded path to support both standalone and managed routes
    const currentPath = this.location.path().split("?")[0];
    const urlTree = this.router.createUrlTree([currentPath]);
    const url = AgFns.buildGridRouteParamsUrl(urlTree, this.idsGridOptions, key && key.toString());
    this.domainService.location.replaceState(url);
  }

  async onIdsRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    this.currentSummaryItem = e.node.data as ItemDetailSummary;
    this.currentSummaryRowNode = e.node as any;

    this.newItdGridOptions.apiX.deselectAll();
    this.currentNewItd = null;

    this.updateLocation();
  }

  async onNewItdRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    this.currentNewItd = e.node.data;
    this.idsGridOptions.apiX.deselectAll();
    this.currentSummaryItem = null;
  }

  addBlankItemDetail() {
    const itdStruct = <ItemDetailProxy>{};
    this.addItemDetailCore(itdStruct);
  }

  addCopyItemDetail() {
    this.stopEditing();
    const copyFrom = this.currentNewItd ?? this.currentSummaryItem;
    if (copyFrom == null) {
      return;
    }
    const itdProxy = <ItemDetailProxy>{};
    itdProxy.product = copyFrom.product;
    itdProxy.account = copyFrom.account;
    if (this.isItdProxy(copyFrom)) {
      itdProxy.itemBin = copyFrom.itemBin;
      itdProxy.qty = copyFrom.qty;

      itdProxy.itemDetailAddons = copyFrom.itemDetailAddons.map((mod) => {
        const newMod = { ...mod };
        newMod.itdProxy = itdProxy;
        newMod.tempId = this.nextTempId++;
        return newMod;
      });
    }
    this.addItemDetailCore(itdProxy);
  }

  isItdProxy(obj: any): obj is ItemDetailProxy {
    return obj.itemBin !== undefined;
  }

  private addItemDetailCore(itdProxy: ItemDetailProxy) {
    itdProxy.tempId = this.nextTempId++;
    itdProxy.itemBin = itdProxy.itemBin ?? this.itemBinDefault;
    itdProxy.qty = itdProxy.qty ?? 1;
    if (itdProxy.itemDetailAddons == null) {
      itdProxy.itemDetailAddons = [];
    }
    this.itdProxies.push(itdProxy);

    if (this.newItdGridOptions.apiX) {
      this.newItdGridOptions.apiX.setGridOption("rowData", this.itdProxies);
      AgFns.sizeColumnsToFit(this.newItdGridOptions);
    }
    this.selectItemDetailRow(itdProxy.tempId.toString(), true);
  }

  selectItemDetailRow(itemDetailId: string, shouldSelect = true) {
    const rowNode = this.newItdGridOptions.apiX.getRowNode(itemDetailId);
    rowNode.setSelected(shouldSelect);
  }

  async selectProduct(itdProxy: ItemDetailProxy) {
    const params = itdProxy.product != null ? { productType: itdProxy.product.productType } : {};
    const r = await EntityFinderDialog.open(ProductFinderDialogComponent, this, params);
    const product = _.first(r) as Product;
    if (product == null) {
      return;
    }
    itdProxy.product = product;
    this.newItdGridOptions.apiX.refreshCells();
  }

  async selectAccount(itdProxy: ItemDetailProxy) {
    const r = await EntityFinderDialog.open(AccountFinderDialogComponent, this, { shouldShowReissueAccountsOnly: true });
    if (_.isEmpty(r)) {
      return;
    }
    itdProxy.account = _.first(r);
    itdProxy.itemDetailAddons = [];
    this.forceRefreshSubgridHack();
    this.newItdGridOptions.apiX.refreshCells();
  }

  async selectAddon(itdAddonProxy: ItemDetailAddonProxy) {
    const accountId = itdAddonProxy.itdProxy.account?.id;
    if (accountId == null) {
      await this.dialogService.showOkMessage("No account specified", "An account is needed in order to select an addon.");
      return;
    }
    const addons = await EntityFinderDialog.open(AddonFinderDialogComponent, this, {
      accountId: accountId,
    });
    if (addons.length == 0) {
      return;
    }
    itdAddonProxy.addon = addons[0];
    this.forceRefreshSubgridHack();

    const rowNode = this.newItdGridOptions.apiX.getRowNode(itdAddonProxy.itdProxy.tempId.toString());
    rowNode.setExpanded(true);
  }

  async deleteItemDetail(itdProxy: ItemDetailProxy) {
    _.remove(this.itdProxies, (itd) => itd == itdProxy);
    this.newItdGridOptions.apiX.setGridOption("rowData", this.itdProxies);
  }

  addItemDetailAddon(itdProxy: ItemDetailProxy) {
    if (itdProxy == null) {
      return;
    }
    const itmProxy = <ItemDetailAddonProxy>{};
    itmProxy.tempId = this.nextTempId++;
    itmProxy.itdProxy = itdProxy;
    itdProxy.itemDetailAddons.push(itmProxy);

    this.forceRefreshSubgridHack();

    const rowNode = this.newItdGridOptions.apiX.getRowNode(itdProxy.tempId.toString());
    rowNode.setExpanded(true);
  }

  async deleteItemDetailAddon(itdaProxy: ItemDetailAddonProxy) {
    const parent = itdaProxy.itdProxy;
    _.remove(parent.itemDetailAddons, (x) => x == itdaProxy);

    this.forceRefreshSubgridHack();

    const rowNode = this.newItdGridOptions.apiX.getRowNode(parent.tempId.toString());
    rowNode.setExpanded(true);
  }

  // canSkipRequery is true if save is called from deactivate
  async saveChanges(canSkipRequery = false) {
    const gridApi = this.stopEditing();

    let totalQty = 0;
    for (let itdProxy of this.itdProxies) {
      if (itdProxy.product == null) {
        const rowNode = gridApi.getRowNode(itdProxy.tempId.toString());
        rowNode.setSelected(true);
        await this.dialogService.showOkMessage("Cannot save", `Product may not be null`);
        return false;
      }
      for (let itmProxy of itdProxy.itemDetailAddons) {
        if (itmProxy.addon == null) {
          const rowNode = gridApi.getRowNode(itdProxy.tempId.toString());
          rowNode.setSelected(true);
          rowNode.setExpanded(true);
          await this.dialogService.showOkMessage("Cannot save", `Modification type may not be null`);
          return false;
        }
      }
      totalQty += itdProxy.qty;
    }
    const nextIds = await this.dbQueryService.getNextItemDetailIds(totalQty);
    let ix = 0;
    const now = new Date(Date.now());
    this.itdProxies.forEach((itdProxy) => {
      for (var i = 0; i < itdProxy.qty; i++) {
        const nextId = nextIds[ix++];
        const itdStruct = <ItemDetail>{};
        itdStruct.accountId = itdProxy.account?.id;
        itdStruct.productId = itdProxy.product.id;
        itdStruct.itemBinId = itdProxy.itemBin.id;
        itdStruct.itemDetailStatusId = ItemDetailStatusEnum.InInventory;
        itdStruct.crtnTs = now;
        itdStruct.id = nextId;
        const itemDetail = this.uow.createEntity(ItemDetail, itdStruct, EntityState.Added);
        itdProxy.itemDetailAddons.forEach((itdAddon) => {
          const itmStruct = <ItemDetailAddon>{};
          itmStruct.itemDetailId = itemDetail.id;
          itmStruct.addonId = itdAddon.addon.id;
          itmStruct.additionalInfo = itdAddon.additionalInfo;
          itmStruct.modUserInit = this.user.initials;
          const itdMod = this.uow.createEntity(ItemDetailAddon, itmStruct, EntityState.Added);
        });
      }
    });
    const r = await this.dbSaveService.saveChanges();
    await this.askPrintBarcodes(r);

    if (!canSkipRequery) {
      this.toastr.success("Save complete - new inventory has been added");
      this.itdProxies = [];
      gridApi.redrawRows();
      this.updateDatasource();
    }
    return true;
  }

  async askPrintBarcodes(saveResult: SaveResult) {
    const itds = <ItemDetail[]>saveResult.entities.filter((e) => e instanceof ItemDetail);
    if (itds.length == 0) {
      return;
    }
    const ok = await this.dialogService.yesNoDialog("Print Barcodes?", "Do you want to print barcodes for the inventory just created?");
    if (ok) {
      await this.barcodeService.printItemDetailBarcodes(itds);
      itds.forEach((itd) => (itd.barcodePrintStatusId = BarcodePrintStatusEnum.PrintedForStock));
      await this.dbSaveService.saveChanges();
    }
  }

  stopEditing() {
    const gridApi = this.newItdGridOptions.apiX;
    gridApi.forEachDetailGridInfo((detailGridInfo) => {
      detailGridInfo.api?.stopEditing();
    });
    gridApi.stopEditing();

    this.currentSummaryRowNode?.setSelected(true);

    return gridApi;
  }

  forceRefreshSubgridHack() {
    // This should NOT be necessary - but the only combination of poking the grid that will allow the subgrid data to display
    // is to call setRowData with an empty collection followed by the real data
    // Note that NEITHER of these calls should be required and certainly not both.
    // Note also that this is not needed to update the main grids - just subgrids when they change after initial draw.
    this.newItdGridOptions.apiX.setGridOption("rowData", []);
    this.newItdGridOptions.apiX.setGridOption("rowData", this.itdProxies);
    // commented out because they only do part of what the 2 lines above do
    // this.newItdGridOptions.apiX.refreshCells( { force: true });
    // this.newItdGridOptions.apiX.redrawRows();
  }

  cancelChanges() {
    const gridApi = this.stopEditing();
    this.itdProxies = [];
    gridApi.redrawRows();
  }
}
